import * as React from 'react';
import { t, type MessageKey } from '@reos/i18n';
import QRCode from 'qrcode';
import { V4Group, V4Switch } from '../ux-v4.js';
import type { ListingDraftView } from '../../lib/listing-draft-api.js';
import { fieldLabel, formatValue } from '../../lib/listing-draft-format.js';
import {
  LISTING_PRINT_PART_ORDER,
  LISTING_PRINT_PRESET_ORDER,
  downloadListingPrintPdf,
  fetchListingPrintPrefs,
  saveListingPrintPrefs,
  type ListingPrintOptions,
  type ListingPrintPrefs,
  type ListingPrintPart,
  type ListingPrintPreset,
} from '../../lib/listing-print-api.js';
import { fetchPublicListingPage } from '../../lib/public-listing-page-api.js';

/**
 * Krok VYV1 — „Tisk a PDF" u nabidky: tri predvolby a u kazde zaskrtavatka,
 * co na papire je.
 *
 * Z prohlidky u maklerky MV reality (29. 10. 2026, bod 2): z Realmana tiskne
 * vyvesku do vylohy BEZ svych udaju a list do sanonu S kontaktem na majitele,
 * a z patnacti variant vybira „nejmene spatnou". Tady je volba viditelna
 * a PAMATUJE SE — pro maklere hned, pro celou kancelar tlacitkem (jen
 * owner/admin, server to overuje znovu).
 *
 * Co se tiskne, pocita SERVER (`effective` v odpovedi prefs): vrstveni
 * „vychozi <- kancelar <- makler" zije jednou v `@reos/reports`.
 */

const cs = { locale: 'cs' } as const;

function tk(key: string, values?: Record<string, string>): string {
  return t(key as MessageKey, values === undefined ? cs : { ...cs, values });
}

/** Nazvy predvoleb jsou TYTEZ, jake nese dokument (`listing.print.preset.*`). */
function presetName(preset: ListingPrintPreset): string {
  return tk(`listing.print.preset.${preset}`);
}

/** Cena a parametry z karty — hotovy text, tentyz jaky vidi makler na radku. */
function priceTextOf(view: ListingDraftView): string | null {
  const price = view.fields.find((field) => field.key === 'asking_price');
  return price === undefined ? null : formatValue(price);
}

function rowsOf(view: ListingDraftView): { label: string; value: string }[] {
  return view.fields
    .filter((field) => field.key !== 'asking_price')
    .map((field) => ({ label: fieldLabel(field.key), value: formatValue(field) ?? '' }))
    .filter((row) => row.value !== '')
    .slice(0, 40);
}

export function PrintCard({ view }: { readonly view: ListingDraftView }): React.JSX.Element {
  /*
   * ZAMERNE BEZ `useQuery`: karta se kresli uvnitr `PublishStep`, ktery se
   * v cizich sadach (`uxd4-pristupnost.spec.tsx`) vykresluje SAMOSTATNE, bez
   * `QueryClientProvider` — `useQuery` by takovy test shodil na „No
   * QueryClient set". Oba dotazy jsou cteni bez navaznosti, takze stav drzi
   * komponenta sama; chyba dotazu kartu jen necha prazdnou (zadne
   * neodchycene odmitnuti, ktere by zcervenalo cizi branu).
   */
  const [prefs, setPrefs] = React.useState<ListingPrintPrefs | null>(null);
  const [publicUrl, setPublicUrl] = React.useState<string | null>(null);
  const [preset, setPreset] = React.useState<ListingPrintPreset>('vyloha');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [savedOffice, setSavedOffice] = React.useState(false);

  const reloadPrefs = React.useCallback(async (): Promise<void> => {
    try {
      setPrefs(await fetchListingPrintPrefs());
    } catch {
      setPrefs(null);
    }
  }, []);

  React.useEffect(() => {
    void reloadPrefs();
  }, [reloadPrefs]);

  React.useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const page = await fetchPublicListingPage(view.caseId);
        if (live) setPublicUrl(page?.publicUrl ?? null);
      } catch {
        if (live) setPublicUrl(null);
      }
    })();
    return () => {
      live = false;
    };
  }, [view.caseId]);

  const options: ListingPrintOptions | null = prefs?.effective[preset] ?? null;

  /** Volba se uklada k MAKLEROVI hned — papir se pak tiskne, jak si zvykl. */
  const save = async (next: ListingPrintOptions): Promise<void> => {
    setError(null);
    setSavedOffice(false);
    const patch = {
      [preset]: { orientation: next.orientation, parts: next.parts, photoCount: next.photoCount },
    } as Parameters<typeof saveListingPrintPrefs>[1];
    try {
      await saveListingPrintPrefs('user', patch);
      await reloadPrefs();
    } catch {
      setError(tk('webAdmin.listingPrint.saveError'));
    }
  };

  const saveForOffice = async (): Promise<void> => {
    if (options === null) return;
    setError(null);
    const patch = {
      [preset]: { orientation: options.orientation, parts: options.parts, photoCount: options.photoCount },
    } as Parameters<typeof saveListingPrintPrefs>[1];
    try {
      await saveListingPrintPrefs('office', patch);
      await reloadPrefs();
      setSavedOffice(true);
    } catch {
      setError(tk('webAdmin.listingPrint.saveError'));
    }
  };

  const download = async (): Promise<void> => {
    if (options === null) return;
    setBusy(true);
    setError(null);
    try {
      // QR kresli prohlizec z adresy verejne stranky nabidky; server dovnitr
      // dokumentu prijme jen `data:` obrazek (Gotenberg nic nestahuje).
      const qrDataUrl =
        options.parts.qr && publicUrl !== null
          ? await QRCode.toDataURL(publicUrl, { margin: 0, width: 320 })
          : null;
      await downloadListingPrintPdf(view.caseId, {
        preset,
        orientation: options.orientation,
        parts: options.parts,
        photoCount: options.photoCount,
        title: view.title,
        priceText: priceTextOf(view),
        rows: rowsOf(view),
        description: view.text.value === '' ? null : view.text.value,
        qrDataUrl,
        publicUrl,
      });
    } catch {
      setError(tk('webAdmin.listingPrint.downloadError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rh-v4-group" data-testid="ld-print">
      <h2 className="rh-ld-subhead">{tk('webAdmin.listingPrint.title')}</h2>
      <p className="rh-ld-foot">{tk('webAdmin.listingPrint.note')}</p>

      <div className="rh-ld-print__presets" role="tablist" aria-label={tk('webAdmin.listingPrint.title')}>
        {LISTING_PRINT_PRESET_ORDER.map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={item === preset}
            className={`rh-v4-btn${item === preset ? '' : ' rh-v4-btn--tinted'}`}
            onClick={() => {
              setPreset(item);
              setSavedOffice(false);
            }}
            data-testid={`ld-print-preset-${item}`}
          >
            {presetName(item)}
          </button>
        ))}
      </div>

      {options === null ? null : (
        <>
          <V4Group testId="ld-print-parts">
            {LISTING_PRINT_PART_ORDER.map((part: ListingPrintPart) => {
              /*
               * Cast, kterou papir UMI, ale nema odkud vzit podklad, se
               * neschovava — stoji tam vypnuta s duvodem (tyz vzor jako
               * portal „připravujeme napojení"): QR potrebuje zapnutou
               * verejnou stranku, mapa dodavatele vyrezu (viz ZBÝVÁ).
               */
              const note =
                part === 'qr' && publicUrl === null
                  ? tk('webAdmin.listingPrint.qrUnavailable')
                  : part === 'map'
                    ? tk('webAdmin.listingPrint.mapUnavailable')
                    : null;
              return (
                <div key={part} className="rh-ld-portal" data-testid={`ld-print-part-${part}`}>
                  <span className="rh-ld-portal__text">
                    <span className="rh-ld-portal__name">{tk(`webAdmin.listingPrint.part.${part}`)}</span>
                    {note === null ? null : <span className="rh-ld-portal__note">{note}</span>}
                  </span>
                  <V4Switch
                    checked={options.parts[part] && note === null}
                    disabled={note !== null}
                    label={tk(`webAdmin.listingPrint.part.${part}`)}
                    onChange={(next) =>
                      void save({ ...options, parts: { ...options.parts, [part]: next } })
                    }
                    testId={`ld-print-switch-${part}`}
                  />
                </div>
              );
            })}
          </V4Group>

          <div className="rh-ld-portal" data-testid="ld-print-photos">
            <span className="rh-ld-portal__text">
              <span className="rh-ld-portal__name">{tk('webAdmin.listingPrint.photos')}</span>
            </span>
            <input
              type="number"
              min={0}
              max={6}
              value={options.photoCount}
              aria-label={tk('webAdmin.listingPrint.photos')}
              onChange={(event) =>
                void save({ ...options, photoCount: Math.max(0, Math.min(6, Number(event.target.value) || 0)) })
              }
              data-testid="ld-print-photo-count"
            />
          </div>

          <div className="rh-ld-portal" data-testid="ld-print-orientation">
            <span className="rh-ld-portal__text">
              <span className="rh-ld-portal__name">{tk('webAdmin.listingPrint.orientation')}</span>
            </span>
            <button
              type="button"
              className="rh-v4-btn rh-v4-btn--tinted"
              onClick={() =>
                void save({
                  ...options,
                  orientation: options.orientation === 'portrait' ? 'landscape' : 'portrait',
                })
              }
              data-testid="ld-print-orientation-toggle"
            >
              {tk(`webAdmin.listingPrint.orientationValue.${options.orientation}`)}
            </button>
          </div>

          <button
            type="button"
            className="rh-v4-btn"
            disabled={busy}
            onClick={() => void download()}
            data-testid="ld-print-download"
          >
            {busy
              ? tk('webAdmin.listingPrint.downloading')
              : tk('webAdmin.listingPrint.download', { preset: presetName(preset) })}
          </button>

          {prefs?.canSetOffice === true ? (
            <button
              type="button"
              className="rh-v4-btn rh-v4-btn--tinted"
              onClick={() => void saveForOffice()}
              data-testid="ld-print-save-office"
            >
              {tk('webAdmin.listingPrint.saveOffice')}
            </button>
          ) : null}

          {savedOffice ? (
            <p className="rh-ld-foot" data-testid="ld-print-saved-office">
              {tk('webAdmin.listingPrint.savedOffice')}
            </p>
          ) : null}
        </>
      )}

      {error === null ? null : (
        <p className="rh-ld-foot" role="alert" data-testid="ld-print-error">
          {error}
        </p>
      )}
    </section>
  );
}
